/**
 * Topbar (TASK-103a) — sticky header in the admin AppShell.
 *
 * Composition (left → right per design §4.2):
 * 1. Burger (mobile only) + wordmark.
 * 2. Breadcrumbs derived from `useMatches()`.
 * 3. Spacer.
 * 4. Notification bell — opens `NotificationCenterDrawer` (TASK-105).
 *    Badge counter is fed by `useNotificationsInboxQuery` (60s background
 *    poll while drawer is closed).
 * 5. User menu (avatar + name + dropdown: profile / change password / logout).
 *
 * The bell + user menu are kept visible on every breakpoint — they are
 * read-only affordances themselves. Truly mutating shell actions (if any are
 * added later) must be wrapped in `<ReadOnlyOnMobile>` per PRD §13.5.
 *
 * The drawer itself is `React.lazy()`-loaded so the inbox UI does not
 * inflate the initial topbar bundle.
 */

import { lazy, Suspense, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ActionIcon,
  Anchor,
  Avatar,
  Breadcrumbs,
  Burger,
  Group,
  Indicator,
  Menu,
  Text,
  Tooltip,
  UnstyledButton,
  useMantineColorScheme,
  type MantineColorScheme,
} from '@mantine/core';
import {
  IconBell,
  IconDeviceDesktop,
  IconLogout,
  IconMoon,
  IconSun,
  IconChevronDown,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconSearch,
} from '@tabler/icons-react';
import { useAuth, useLogout } from '@/hooks/use-auth';
import { useResponsive } from '@/lib/responsive-context';
import {
  INBOX_BADGE_REFETCH_MS,
  useNotificationsInboxQuery,
} from '@/api/notifications';
import { ROUTE_TITLES } from './route-titles';
import classes from './Topbar.module.css';

const NotificationCenterDrawer = lazy(() =>
  import('@/components/notifications').then((m) => ({
    default: m.NotificationCenterDrawer,
  })),
);

interface TopbarProps {
  mobileOpened: boolean;
  onMobileToggle: () => void;
  /** Opens the global Command Palette ⌘K (TASK-104). */
  onOpenCommandPalette: () => void;
  /** Desktop sidebar collapse toggle (Wave C UX pass). On mobile the burger
   *  takes over and these props are ignored. */
  sidebarCollapsed: boolean;
  onSidebarToggle: () => void;
}

interface Crumb {
  label: string;
  to: string;
  isLast: boolean;
}

function buildBreadcrumbs(pathname: string): Crumb[] {
  const parts = pathname.split('/').filter(Boolean);
  if (parts.length === 0) return [];
  return parts.map((segment, idx) => {
    const to = '/' + parts.slice(0, idx + 1).join('/');
    const label = ROUTE_TITLES[to] ?? decodeURIComponent(segment);
    return { label, to, isLast: idx === parts.length - 1 };
  });
}

const COLOR_SCHEME_OPTIONS: ReadonlyArray<{
  value: MantineColorScheme;
  label: string;
  Icon: typeof IconSun;
}> = [
  { value: 'light', label: 'Светлая', Icon: IconSun },
  { value: 'dark', label: 'Тёмная', Icon: IconMoon },
  { value: 'auto', label: 'Системная', Icon: IconDeviceDesktop },
];

export function Topbar({
  mobileOpened,
  onMobileToggle,
  onOpenCommandPalette,
  sidebarCollapsed,
  onSidebarToggle,
}: TopbarProps) {
  const { user } = useAuth();
  const logout = useLogout();
  const location = useLocation();
  const { isMobile } = useResponsive();
  const { colorScheme, setColorScheme } = useMantineColorScheme();
  const crumbs = buildBreadcrumbs(location.pathname);
  const displayName = user?.display_name ?? user?.email ?? 'Гость';

  const [drawerOpened, setDrawerOpened] = useState(false);
  // Background poll for the badge — pauses while the drawer is open
  // (the drawer's own query takes over on a faster cadence).
  const badgeQuery = useNotificationsInboxQuery({
    refetchIntervalMs: INBOX_BADGE_REFETCH_MS,
    enabled: !drawerOpened,
  });
  const unreadCount = badgeQuery.data?.unread_count ?? 0;
  const bellAriaLabel = `Уведомления, ${unreadCount} непрочитанных`;

  return (
    <Group h="100%" px="md" gap="md" wrap="nowrap" className={classes.topbar}>
      {isMobile ? (
        <Burger
          opened={mobileOpened}
          onClick={onMobileToggle}
          size="sm"
          aria-label={mobileOpened ? 'Закрыть меню' : 'Открыть меню'}
        />
      ) : null}

      <Anchor
        component={Link}
        to="/"
        underline="never"
        className={classes.wordmark}
        aria-label="musichead — на главную"
      >
        musichead
      </Anchor>

      {isMobile ? null : (
        <Tooltip
          label={sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
          withArrow
        >
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            aria-label={sidebarCollapsed ? 'Развернуть меню' : 'Свернуть меню'}
            aria-pressed={sidebarCollapsed}
            onClick={onSidebarToggle}
          >
            {sidebarCollapsed ? (
              <IconLayoutSidebarLeftExpand size={20} stroke={1.5} />
            ) : (
              <IconLayoutSidebarLeftCollapse size={20} stroke={1.5} />
            )}
          </ActionIcon>
        </Tooltip>
      )}

      {isMobile ? (
        <Tooltip label="Поиск (⌘K)" withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            aria-label="Открыть командную палитру"
            onClick={onOpenCommandPalette}
          >
            <IconSearch size={20} stroke={1.5} />
          </ActionIcon>
        </Tooltip>
      ) : (
        <UnstyledButton
          className={classes.searchTrigger}
          onClick={onOpenCommandPalette}
          aria-label="Открыть командную палитру"
          aria-keyshortcuts="Meta+K Control+K"
        >
          <IconSearch size={16} stroke={1.5} aria-hidden />
          <Text component="span" className={classes.searchTriggerLabel}>
            Поиск…
          </Text>
          <Text component="span" className={classes.searchTriggerKbd} aria-hidden>
            ⌘K
          </Text>
        </UnstyledButton>
      )}

      {crumbs.length > 0 && !isMobile ? (
        <Breadcrumbs
          separator="/"
          className={classes.breadcrumbs}
          aria-label="Хлебные крошки"
        >
          {crumbs.map((c) =>
            c.isLast ? (
              <Text key={c.to} size="sm" c="dimmed">
                {c.label}
              </Text>
            ) : (
              <Anchor
                key={c.to}
                component={Link}
                to={c.to}
                size="sm"
                c="dimmed"
              >
                {c.label}
              </Anchor>
            ),
          )}
        </Breadcrumbs>
      ) : null}

      <div className={classes.spacer} />

      <Tooltip label="Уведомления" withArrow>
        <Indicator
          inline
          size={16}
          offset={4}
          color="red"
          label={unreadCount > 99 ? '99+' : unreadCount}
          disabled={unreadCount === 0}
        >
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            aria-label={bellAriaLabel}
            onClick={() => setDrawerOpened(true)}
          >
            <IconBell size={20} stroke={1.5} />
          </ActionIcon>
        </Indicator>
      </Tooltip>

      <Suspense fallback={null}>
        {drawerOpened ? (
          <NotificationCenterDrawer
            opened={drawerOpened}
            onClose={() => setDrawerOpened(false)}
          />
        ) : null}
      </Suspense>

      <Menu position="bottom-end" shadow="md" width={220} withArrow>
        <Menu.Target>
          <UnstyledButton
            className={classes.userButton}
            aria-label={`Меню пользователя ${displayName}`}
          >
            <Group gap="xs" wrap="nowrap">
              <Avatar
                size={28}
                radius="xl"
                color="indigo"
                name={displayName}
              />
              {!isMobile ? (
                <>
                  <Text size="sm" fw={500} lineClamp={1} maw={140}>
                    {displayName}
                  </Text>
                  <IconChevronDown size={14} stroke={1.5} />
                </>
              ) : null}
            </Group>
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>{user?.email ?? 'Аккаунт'}</Menu.Label>
          {/* «Профиль» и «Сменить пароль» убраны до появления роутов
              (/profile + /settings/password) — обещали без action и сбивали
              пользователя. Вернуть, когда страницы будут готовы. */}
          <Menu.Label>Тема</Menu.Label>
          {COLOR_SCHEME_OPTIONS.map((option) => {
            const Icon = option.Icon;
            const isActive = colorScheme === option.value;
            return (
              <Menu.Item
                key={option.value}
                leftSection={<Icon size={16} stroke={1.5} />}
                onClick={() => setColorScheme(option.value)}
                aria-pressed={isActive}
                fw={isActive ? 600 : undefined}
                c={isActive ? 'indigo' : undefined}
              >
                {option.label}
              </Menu.Item>
            );
          })}
          <Menu.Divider />
          <Menu.Item
            color="red"
            leftSection={<IconLogout size={16} stroke={1.5} />}
            onClick={() => logout.mutate()}
            disabled={logout.isPending}
          >
            Выйти
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
}
